import type { OrderLifecycleWire, OrderSummaryState } from '@croissants/contracts';
import {
  Badge,
  type BadgeProps,
  orderSummaryReasonOf,
  type PillFamily,
  type PillParamsProp,
  type PillReasonProps,
  type PillState,
  pillReasonText,
  pillTone,
  pillWord,
} from '@croissants/ui';
import type { JSX } from 'react';
import { useLocale, useT } from '../i18n/index';
import { time } from '../lib/format';

export type StatusPillProps<F extends PillFamily, S extends PillState<F> = PillState<F>> = Omit<
  BadgeProps,
  'tone' | 'children'
> & {
  readonly family: F;
  readonly state: S;
  /**
   * The state was ASKED FOR and is not confirmed yet: the pill is drawn grey
   * and "(saving)" written beside it, because an operations screen that
   * asserts a write the server may still refuse is worse than one that says it
   * is waiting (docs/portal-latency-audit.html). "(saving)" is a sentence about
   * the row, not a state, so it is text beside the pill, never inside it.
   */
  readonly pending?: boolean;
} & PillParamsProp<F, S> &
  PillReasonProps<F, S>;

/**
 * Internal Ops' status pill (#2004): the shared list's word and colour, in
 * English - Internal Ops is English-only - drawn as a pill whatever its length
 * (every approved word is one or two words). The reason beside a state is
 * ALWAYS shown on Internal: a state with one reason prints it without being
 * asked, and a state whose reason depends on the record prints the one the
 * screen names (`reason=`).
 */
export function StatusPill<F extends PillFamily, S extends PillState<F>>(
  props: StatusPillProps<F, S>,
): JSX.Element {
  const t = useT();
  const { family, state, pending, params, reason, withReason, reasonParams, ...badge } =
    props as StatusPillProps<F, S> & {
      readonly params?: never;
      readonly reasonParams?: never;
    };
  // The params were typed against the word by `PillParamsProp`; this call only forwards them.
  const word = (pillWord as (f: F, s: S, l: 'en', p?: unknown) => string)(
    family,
    state,
    'en',
    params,
  );
  const pill = (
    <Badge {...badge} tone={pending === true ? 'grey' : pillTone(family, state)}>
      {word}
    </Badge>
  );
  const because =
    withReason === false && reason === undefined
      ? null
      : pillReasonText(family, state, 'en', reason, reasonParams);
  const aside = [because, pending === true ? t('pending.suffix') : null].filter(
    (text): text is string => text !== null,
  );
  if (aside.length === 0) return pill;
  return (
    <span className="cib-pill-with-reason">
      {pill}
      <span className="cib-pill-reason">{aside.join(' ')}</span>
    </span>
  );
}

/**
 * An order's summary state with WHY beside it (#2004, approved 2026-10-08,
 * section 3): the cause the order recorded beside Canceled ("No bakery
 * accepted", "Card refused"), and "No courier yet, searching until 07:45"
 * beside a Ready order whose courier search is running - on the Admin
 * dashboard, the Orders list and the order record alike, through the one
 * `orderSummaryReasonOf`.
 */
export function OrderStatePill({
  state,
  lifecycle,
  courierSearchDeadline = null,
}: {
  readonly state: OrderSummaryState;
  readonly lifecycle: Pick<OrderLifecycleWire, 'cancelledCause'>;
  readonly courierSearchDeadline?: string | null;
}): JSX.Element {
  const { locale } = useLocale();
  const because = orderSummaryReasonOf(
    state,
    { cancelledCause: lifecycle.cancelledCause ?? null, courierSearchDeadline },
    (instant) => time(instant, locale),
  );
  return <StatusPill family="orderSummary" state={state} {...because} />;
}
